import { formatPrice } from "@/helpers/formatPrice";
import { usePaymentId } from "@/services/payment/queries";
import { PaymentReceiptItem } from "@/types/payment";
import { paymentMethodLabel } from "@/views/private/PaymentsPrivate";
import { PrinterOutlined } from "@ant-design/icons";
import { Button, Descriptions, message, Modal, Table, Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import { forwardRef, useImperativeHandle, useState } from "react";
import { ErrorResult } from "../ErrorResult";
import { LoadingSection } from "../loadings/LoadingSection";

export interface ModalVPaymentRef {
  childFunction: (register_id: string) => void;
}

export const ModalVPayment = forwardRef<ModalVPaymentRef>((props, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const [id, setId] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();

  const { data, isLoading, isFetching, isError, error, refetch } =
    usePaymentId(id);

  const childFunction = (register_id: string) => {
    setOpen(true);
    setId(register_id);
    setTimeout(() => {
      void refetch();
    }, 0);
  };

  useImperativeHandle(ref, () => ({
    childFunction,
  }));

  const payment_method_config = data
    ? (paymentMethodLabel[data.method] ?? {
        label: data.method,
        color: "default",
      })
    : {
        label: "-",
        color: "default",
      };

  const columns: ColumnsType<PaymentReceiptItem> = [
    {
      title: "Producto",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Cantidad",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
    },
    {
      title: "Precio",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (value: number) => formatPrice(value),
    },
    {
      title: "Notas",
      dataIndex: "notes",
      key: "notes",
      render: (value: string | null) => value ? <span className="text-amber-400 font-medium italic">📝 {value}</span> : "-",
    },
  ];

  const escapeHtml = (value: string) =>
    value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const printHtml = (html: string) => {
    try {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("aria-hidden", "true");
      iframe.style.position = "fixed";
      iframe.style.left = "-10000px";
      iframe.style.top = "0";
      iframe.style.width = "800px";
      iframe.style.height = "600px";
      iframe.style.border = "0";
      iframe.style.opacity = "0";
      iframe.style.pointerEvents = "none";

      const cleanup = () => {
        setTimeout(() => {
          iframe.remove();
        }, 1_000);
      };

      document.body.appendChild(iframe);

      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) {
        messageApi.error("No se pudo abrir la impresión");
        cleanup();
        return;
      }

      doc.open();
      doc.write(html);
      doc.close();

      const printNow = () => {
        win.focus();
        win.print();
        cleanup();
      };

      if ("fonts" in doc) {
        void (
          doc as unknown as { fonts: { ready: Promise<unknown> } }
        ).fonts.ready
          .catch(() => null)
          .finally(() => setTimeout(printNow, 50));
        return;
      }

      setTimeout(printNow, 150);
    } catch (error) {
      console.error(error);
      messageApi.error("Ocurrió un error al preparar la impresión");
    }
  };

  const buildReceiptHtml = (params: {
    title: string;
    isTakeaway?: boolean;
    tableNumber: number;
    zone?: string;
    waiter: string;
    dateText: string;
    items: { productName: string; quantity: number; unitPrice: number }[];
    subtotal: number;
    discount: number;
    tip: number;
    tax: number;
    total: number;
    footer?: string;
    metaRight?: string;
  }) => {
    const linesHtml = params.items
      .map((item) => {
        const qty = Number(item.quantity) || 0;
        const unit = Number(item.unitPrice) || 0;
        const lineTotal = qty * unit;
        return `
          <tr class="item">
                     <td class="qty">${qty}x</td>
                     <td class="name">${escapeHtml(String(item.productName ?? ""))}</td>
                     <td class="amt">${escapeHtml(formatPrice(unit))}</td>
                     <td class="amt">${escapeHtml(formatPrice(lineTotal))}</td>
                   </tr>
        `;
      })
      .join("");

    const finalSubtotal = Number(params.subtotal) + Number(params.tax);
    const subtotalText = formatPrice(finalSubtotal);
    const discountText =
      params.discount > 0 ? formatPrice(params.discount) : null;
    const tipText = params.tip > 0 ? formatPrice(params.tip) : null;
    const taxText = formatPrice(params.tax);
    const totalText = formatPrice(params.total);

    return `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${escapeHtml(params.title)} - Mesa ${params.tableNumber}</title>
         <style>
          @page { margin: 8mm; }
          body {
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            color: #111;
          }
          .receipt {
            width: 72mm;
            max-width: 100%;
          }
          h1 {
            font-size: 14px;
            margin: 0 0 6px 0;
            text-align: center;
          }
          .meta {
            font-size: 11px;
            line-height: 1.35;
            margin-bottom: 8px;
          }
          .metaRow {
            display: flex;
            justify-content: space-between;
            gap: 8px;
          }
          .rule {
            border-top: 1px dashed #333;
            margin: 8px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }
          th,
          td {
            padding: 3px 0;
            vertical-align: top;
          }
          thead th {
            font-weight: 700;
            text-align: left;
            padding-bottom: 4px;
          }
          .separator td {
            border-top: 1px dashed #333;
            padding: 0;
            height: 6px;
          }
          .qty {
            width: 10mm;
          }
          .name {
            width: auto;
            padding-right: 6px;
          }
          .unit {
            width: 18mm;
            text-align: right;
            white-space: nowrap;
          }
          .amt {
            width: 18mm;
            text-align: right;
            white-space: nowrap;
          }
          .totals td {
            padding: 2px 0;
          }
          .totals .label {
            text-align: left;
          }
          .totals .value {
            text-align: right;
            white-space: nowrap;
          }
          .grand {
            font-size: 13px;
            font-weight: 700;
          }
          .center {
            text-align: center;
          }
        </style>
        </head>
        <body>
          <div class="receipt">
            <h1>${escapeHtml(params.title)}</h1>
             <div class="meta">
              <div class="metaRow">
                <div>
                  <strong>${params.isTakeaway ? "Cliente:" : "Mesa:"}</strong>
                  ${escapeHtml(String(params.tableNumber))}
                                  </div>
                ${params.metaRight ? `<div>${escapeHtml(params.metaRight)}</div>` : ""}
              </div>
              <div><strong>Mesero:</strong> ${escapeHtml(String(params.waiter ?? ""))}</div>
              <div><strong>Fecha:</strong> ${escapeHtml(params.dateText)}</div>
            </div>
            <div class="rule"></div>
            <table>
            <thead>
                <tr class="header">
                  <th class="qty">Uds</th>
                  <th class="name">Descripción</th>
                  <th class="unit">P. U.</th>
                  <th class="amt">Total</th>
                </tr>
              </thead>
              <tbody>
                ${linesHtml}
              </tbody>
            </table>
            <div class="rule"></div>
           <table class="totals">
              <tbody>
                <tr><td class="label">Subtotal</td><td class="value">${escapeHtml(subtotalText)}</td></tr>
                ${discountText ? `<tr><td class="label">Descuento</td><td class="value">- ${escapeHtml(discountText)}</td></tr>` : ""}
                ${tipText ? `<tr><td class="label">Propina</td><td class="value">${escapeHtml(tipText)}</td></tr>` : ""}
                <tr><td class="label grand">TOTAL</td><td class="value grand">${escapeHtml(totalText)}</td></tr>
              </tbody>
            </table>
            <div class="rule"></div>
            ${params.footer ? `<div class="meta center">${escapeHtml(params.footer)}</div>` : ""}
          </div>
        </body>
      </html>`;
  };

  const handlePrint = () => {
    const receiptItems = data?.meta?.receipt?.items ?? [];
    if (receiptItems.length === 0) {
      messageApi.error("El pago no tiene detalle para reimprimir");
      return;
    }

    const paidAt = data?.paid_at ? new Date(data?.paid_at) : new Date();

    const isTakeAway = data?.client_name ? true : false;

    const html = buildReceiptHtml({
      title: "COMPROBANTE",
      isTakeaway: Boolean(isTakeAway),
      tableNumber: isTakeAway ? data?.client_name! : data?.table_number!,
      waiter: data?.meta?.receipt?.waiter ?? "-",
      dateText: paidAt.toLocaleString(),
      items: receiptItems.map((i) => ({
        productName: i.product_name,
        quantity: i.quantity,
        unitPrice: i.unit_price,
        notes: i.notes,
      })),
      subtotal: data?.subtotal ?? 0,
      discount: data?.discount ?? 0,
      tip: data?.tip ?? 0,
      tax: data?.tax ?? 0,
      total: data?.total ?? 0,
      footer: "Gracias por su visita",
      metaRight: `Pago: ${(data?.method ?? "-").toUpperCase()}`,
    });
    printHtml(html);
    messageApi.success("Se envió la solicitud de impresión");
  };

  return (
    <Modal
      title={"Detalle de pago"}
      centered
      open={open}
      width={800}
      onCancel={() => setOpen(false)}
      footer={<Button onClick={() => setOpen(false)}>Cerrar</Button>}
    >
      {contextHolder}
      {isLoading || isFetching ? (
        <div className="flex pt-5">
          <LoadingSection />
        </div>
      ) : isError ? (
        <ErrorResult error={error} refetch={refetch} />
      ) : null}

      {!isError && !isLoading && !isFetching && (
        <>
          <Descriptions
            size="small"
            column={{
              xs: 1,
              sm: 1,
              md: 2,
              lg: 2,
              xl: 2,
              xxl: 2,
            }}
            className="mb-4"
          >
            <Descriptions.Item label="Ticket">
              {data?.number_ticket ? (
                data?.number_ticket
              ) : (
                <Tag color="red">Sin N° Ticket</Tag>
              )}
            </Descriptions.Item>

            <Descriptions.Item
              label={data?.client_name ? "Para llevar" : "Mesa"}
            >
              {data?.client_name ? (
                <>
                  <span style={{ marginLeft: 8 }}>{data.client_name}</span>
                </>
              ) : data?.table_number ? (
                data.table_number
              ) : (
                <Tag color="red">Sin mesa</Tag>
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Total">
              {formatPrice(data?.total)}
            </Descriptions.Item>

            <Descriptions.Item label="Metodo de pago">
              <Tag color={payment_method_config.color}>
                {payment_method_config.label}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Estado de Pago">
              <Tag color={data?.status === "paid" ? "green" : "orange"}>
                {data?.status === "paid" ? "Pagado" : "Pendiente"}
              </Tag>
            </Descriptions.Item>

            {data?.invoice_number && (
              <Descriptions.Item label="N° Factura">
                <Tag color="blue">{data.invoice_number}</Tag>
              </Descriptions.Item>
            )}

            <Descriptions.Item label="Fecha">
              {data?.paid_at
                ? new Date(data?.paid_at).toLocaleString("es-CL")
                : "Sin fecha"}
            </Descriptions.Item>
          </Descriptions>

          <div className="my-4">
            <h3 className="text-base font-semibold mb-2">Cliente</h3>

            {data?.customer ? (
              <Descriptions
                size="small"
                column={{
                  xs: 1,
                  sm: 1,
                  md: 2,
                  lg: 3,
                  xl: 3,
                  xxl: 3,
                }}
              >
                <Descriptions.Item label="Tipo de cliente">
                  {data.customer.type === "person" ? "Persona" : "Empresa"}
                </Descriptions.Item>

                <Descriptions.Item label="RUN">
                  {data.customer.tax_id}
                </Descriptions.Item>

                <Descriptions.Item label="Nombre">
                  {data.customer.name}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <p className="text-gray-500">No hay cliente asociado</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => {
                handlePrint();
              }}
            >
              <PrinterOutlined className="text-lg" />
              Reimprimir
            </Button>
          </div>

          <Table
            rowKey={(record, index) => `${record.product_name}-${index}`}
            columns={columns}
            className="mt-4"
            dataSource={data?.meta?.receipt?.items || []}
            pagination={false}
            scroll={{ x: 350 }}
            summary={() => {
              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <span>Subtotal</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {formatPrice(data?.subtotal)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  <Table.Summary.Row>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <span>Descuento</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {formatPrice(data?.discount)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  <Table.Summary.Row>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <span>IVA</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {formatPrice(data?.tax)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  <Table.Summary.Row>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <span>Propina</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {formatPrice(data?.tip)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  <Table.Summary.Row>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <span>Total</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {formatPrice(data?.total)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </>
      )}
    </Modal>
  );
});
